import algoliasearch from 'algoliasearch/lite';
import { SearchOptions, SearchResponse } from '@algolia/client-search';
import { pathOr } from 'ramda';
import queryString from 'query-string';
import { deepEqual } from './deepEqual';
import { IFacet } from '.';

export interface ISearchParams<TFacets extends string> {
	textSearch: string;
	/** The list of facets available */
	facets?: TFacets[];
	/** The list of facets with count */
	countFacets?: TFacets[];
	/** The selected facets */
	facetsData?: Record<TFacets, string[]>;
	/** The number of results per page, if not set, will default to 50 */
	perPage?: number;
	/** Any number filter */
	numericFilters?: any;
	filters?: string;
}

interface ISearchConfig<TFacets extends string> {
	indexName: string;
	defaultSearchParams: ISearchParams<TFacets>;
	facets?: string[];
	/** Handle URL url management */
	urlMap?: { [key: string]: string };
}

const defaultPerPage = 24;

/**
 * Filters that need to be excluded from the facet search
 */
enum EExceptionFilters {
	SUBURBS = 'suburbs',
}
const exceptionFilters: EExceptionFilters[] = [EExceptionFilters.SUBURBS];

/**
 * Search services to integrate with an Algolia index
 */
export class SearchService<TFacets extends string, ISearchResult> {
	protected _config: ISearchConfig<TFacets>;
	protected _searchParams: ISearchParams<TFacets>;
	protected _facets: string[] = [];
	protected _facetsCount: Record<TFacets, Record<string, number>>;
	protected _algoliaResults: SearchResponse<ISearchResult>;
	protected _searchIndex;
	protected _listenersIndex = 0;
	protected _listeners = new Map<
		number,
		(result: SearchResponse<ISearchResult>) => void
	>();
	protected _isLoading = false;
	protected _currentPage = 0;
	protected _maxPages = 0;

	public constructor(config: ISearchConfig<TFacets>) {
		const searchClient = algoliasearch(
			(window as any).algolia.application_id,
			(window as any).algolia.search_api_key,
		);

		this._config = config;
		this._searchIndex = searchClient.initIndex(config.indexName);
		this._searchParams = { textSearch: '' };
		this._facetsCount = {} as any;
		if (config.facets) {
			this._facets = config.facets;
		}
		if (config.urlMap) {
			this.handleUrlMap(config.urlMap);

			window.onpopstate = () => {
				this.handleUrlMap(config.urlMap);
			};
		} else {
			this.updateSearchParam(this.config.defaultSearchParams, true);
		}
	}

	get searchParams(): ISearchParams<TFacets> {
		return JSON.parse(JSON.stringify(this._searchParams));
	}

	set searchParams(newParams: ISearchParams<TFacets>) {
		this.updateSearchParam(newParams);
	}

	get algoliaResults(): SearchResponse<ISearchResult> | null {
		return JSON.parse(JSON.stringify(this._algoliaResults || null));
	}

	get config(): ISearchConfig<TFacets> {
		return JSON.parse(JSON.stringify(this._config));
	}

	get facetCounts(): Record<TFacets, number> {
		return JSON.parse(JSON.stringify(this._facetsCount));
	}

	get currentPage() {
		return this._currentPage;
	}

	get maxPages() {
		return this._maxPages;
	}

	get isSearched(): boolean {
		return !deepEqual(this._searchParams, this._config.defaultSearchParams);
	}

	get defaultSearch(): ISearchParams<TFacets> {
		return JSON.parse(JSON.stringify(this._config.defaultSearchParams));
	}

	set indexName(indexName: string) {
		const searchClient = algoliasearch(
			(window as any).algolia.application_id,
			(window as any).algolia.search_api_key,
		);
		this._searchIndex = searchClient.initIndex(indexName);

		this.queryAlgolia().then(() => {
			this.updateListeners();
		});
	}

	public addResultsListener(
		listener: (result: SearchResponse<ISearchResult>) => void,
	) {
		++this._listenersIndex;
		this._listeners.set(this._listenersIndex, listener);
		return this._listenersIndex;
	}

	public removeResultsListener(index: number) {
		if (this._listeners.has(index)) {
			this._listeners.delete(index);
		}
	}

	public async loadMore() {
		if (!this._isLoading && this._currentPage < this._maxPages) {
			this._isLoading = true;
			++this._currentPage;

			const result = await this.queryResults();
			// Add the hits to the current result
			this._algoliaResults.hits.push(...result.hits);
			this._isLoading = false;
			this.updateListeners();
		}
	}

	public async loadPreviousPage() {
		if (!this._isLoading && this._currentPage < this._maxPages) {
			this._isLoading = true;
			--this._currentPage;

			const result = await this.queryResults();

			this._algoliaResults.hits = result.hits;
			this._isLoading = false;
			this.updateListeners();
		}
	}

	public async loadNextPage() {
		if (!this._isLoading && this._currentPage < this._maxPages) {
			this._isLoading = true;
			++this._currentPage;

			const result = await this.queryResults();

			this._algoliaResults.hits = result.hits;
			this._isLoading = false;
			this.updateListeners();
		}
	}

	public async loadPage(pageNumber: number) {
		if (!this._isLoading && this._currentPage < this._maxPages) {
			this._isLoading = true;
			this._currentPage = pageNumber;

			const result = await this.queryResults();
			// Add the hits to the current result
			this._algoliaResults.hits = result.hits;
			this._isLoading = false;
			this.updateListeners();
		}
	}

	public getUserFacets() {
		const result: IFacet[] = [];
		const searchParams = this.searchParams;
		const defaultParams = this.config.defaultSearchParams;
		// Do a difference between the default facets and the current search facets
		if (searchParams.facetsData) {

			for (const facetName in searchParams.facetsData) {
				if (
					Object.prototype.hasOwnProperty.call(
						searchParams.facetsData,
						facetName,
					)
				) {
					let facetValues = searchParams.facetsData[facetName];

					const defaultFacets: string[] | null = pathOr(
						null,
						['facetsData', facetName],
						defaultParams,
					);

					// Filter out the default facets
					if (defaultFacets) {
						facetValues = facetValues.filter((singleFacet) => {
							return defaultFacets.indexOf(singleFacet) < 0;
						});
					}

					result.push({
						facetName,
						facetValue: facetValues,
					});
				}
			}
		}

		return result;
	}

	protected updateSearchParam(
		newParams: ISearchParams<TFacets>,
		skipUrlUpdate?: boolean,
	) {
		// Compare the 2 objects
		if (!deepEqual(this._searchParams, newParams)) {
			this._searchParams = newParams;
			if (this._config.urlMap) {
				// Get the user's facets
				const userFacets = this.getUserFacets();
				const query: Record<string, string[]> = {} as any;
				if (userFacets.length) {
					userFacets.forEach((facet) => {
						if (facet.facetName in this._config.urlMap) {
							if (!(facet.facetName in query)) {
								query[this._config.urlMap[facet.facetName]] =
									[];
							}
							query[this._config.urlMap[facet.facetName]] =
								facet.facetValue;
						}
					});
				}

				const stringifiedParams = queryString.stringify(query, {
					arrayFormat: 'index',
				});

				// Update url
				if (location.search !== stringifiedParams) {
					const urlPath =
						location.origin +
						location.pathname +
						'?' +
						stringifiedParams;
					window.history.pushState({}, '', urlPath);

					// query algolia
					this.queryAlgolia().then(() => {
						this.updateListeners();
					});

					return;
				}
			}
			this.queryAlgolia().then(() => {
				this.updateListeners();
			});
		}
	}

	public async getFacetCounts() {
		await this.queryAlgolia();

		return this._facetsCount;
	}

	public hasSearchParams(): boolean {
		const params = this.getUserFacets();

		for (let index = 0; index < params.length; index++) {
			const element = params[index];
			const { facetValue } = element;

			if (facetValue.length > 0) {
				return true;
			}
		}

		return false;
	}

	public getAlgoliaQueryParams(): SearchOptions {
		return {
			facetFilters: this.formatFacetFilters(),
			facets: this._searchParams.countFacets,
			hitsPerPage: this._searchParams.perPage
				? this._searchParams.perPage
				: defaultPerPage,
			numericFilters: this._searchParams.numericFilters,
			page: this._currentPage,
			maxValuesPerFacet: 100,
			facetingAfterDistinct: true,
		};
	}

	public getSearchIndex() {
		return this._searchIndex;
	}

	public formatFacetData(facetsData: Record<TFacets, string[]>): string[][] {
		const facetFilters: string[][] = [];

		if (facetsData) {
			for (const key in facetsData) {
				if (exceptionFilters.indexOf(key as EExceptionFilters) < 0) {
					let facetQuery: string[] = [];
					if (key in facetsData) {
						const element = facetsData[key];

						element.forEach((facetValue) => {
							if (facetValue !== '') {
								facetQuery.push(`${key}:${facetValue}`);
							}
						});
					}
					if (facetQuery.length) {
						facetFilters.push(facetQuery);
					}
				}
			}
		}

		return facetFilters;
	}

	protected async queryResults() {
		const result = await this._searchIndex.search<ISearchResult>(
			this._searchParams.textSearch,
			this.getAlgoliaQueryParams(),
		);
		return result;
	}

	protected async queryAlgolia() {
		if (!this._isLoading) {
			this._isLoading = true;
			this._currentPage = 0;
			const result = await this.queryResults();

			const newFacetCount: Record<
				TFacets,
				Record<string, number>
			> = {} as any;

			if (
				this._searchParams.countFacets &&
				this._searchParams.countFacets.length
			) {
				for (
					let i = 0;
					i < this._searchParams.countFacets.length;
					i++
				) {
					const facetToSkip = this._searchParams.countFacets[i];

					// Perform facet counts queries
					const facetResult =
						await this._searchIndex.search<ISearchResult>(
							this._searchParams.textSearch,
							{
								facetFilters:
									this.facetFiltersSkip(facetToSkip),
								facets: this._searchParams.countFacets,
								hitsPerPage: 50,
							},
						);

					if (
						facetResult &&
						'facets' in facetResult &&
						facetResult.facets &&
						facetToSkip in facetResult.facets
					) {
						newFacetCount[facetToSkip] =
							facetResult.facets[facetToSkip];
					}
				}
			}
			this._maxPages = result.nbPages;
			this._algoliaResults = result;
			this._facetsCount = newFacetCount;
			this._isLoading = false;
		}
	}

	protected updateListeners() {
		// update the listeners
		this._listeners.forEach((listener) => {
			listener(this.algoliaResults);
		});
	}

	protected formatFacetFilters(): string[][] {
		return this.formatFacetData(this._searchParams.facetsData);
	}

	protected facetFiltersSkip(facetToSkip: string): string[][] {
		const facetFilters: string[][] = [];

		if (this._searchParams.facetsData) {
			let facetQuery: string[] = [];
			for (const key in this._searchParams.facetsData) {
				if (
					Object.prototype.hasOwnProperty.call(
						this._searchParams.facetsData,
						key,
					) &&
					key !== facetToSkip
				) {
					const elements: string[] =
						this._searchParams.facetsData[key];

					elements.forEach((element) => {
						facetQuery.push(`${key}:${element}`);
					});
				}
			}

			if (facetQuery.length) {
				facetFilters.push(facetQuery);
			}
		}

		return facetFilters;
	}

	protected handleUrlMap(urlMap: Record<string, string>) {
		const search = location.search;
		if (search) {
			const newSearchParams = this.config.defaultSearchParams;

			const parsedSearch = queryString.parse(search, {
				arrayFormat: 'index',
			});

			for (const key in parsedSearch) {
				if (Object.prototype.hasOwnProperty.call(parsedSearch, key)) {
					const facetValue = parsedSearch[key];

					// Check if the key in is the URL map
					let facetName = null;
					for (const name in urlMap) {
						if (
							Object.prototype.hasOwnProperty.call(urlMap, name)
						) {
							const mappedName = urlMap[name];
							if (mappedName === key) {
								facetName = name;
							}
						}
					}

					if (facetName) {
						if (!newSearchParams.facetsData) {
							newSearchParams.facetsData = {} as any;
						}
						if (!(facetName in newSearchParams.facetsData)) {
							newSearchParams.facetsData[facetName] = [];
						}
						newSearchParams.facetsData[facetName] = facetValue;
					}
				}
			}
			this.updateSearchParam(newSearchParams, true);
		} else {
			this.updateSearchParam(this.config.defaultSearchParams, true);
		}
	}
}



// WEBPACK FOOTER //
// ./scripts/algolia/SearchService.ts